// Product onboarding + profile fields (Firestore `users` docs, snake_case from Scanbrix app).

export const WORK_ROLE_LABELS = {
  architect: "Architect",
  designer: "Designer",
  remodeler_contractor: "Remodeler / Contractor",
  contractor: "Contractor",
  design_build: "Design / Build",
  other: "Other",
};

export const PROJECT_TYPE_LABELS = {
  commercial: "Commercial",
  residential: "Residential",
  adaptive_reuse: "Adaptive re-use",
  adaptive_reuse_project: "Adaptive re-use",
  other: "Other",
};

export const PREFERRED_FORMAT_LABELS = {
  sketchup: "SketchUp",
  revit: "Revit",
  autocad: "AutoCAD",
  autodesk: "Autodesk",
  other: "Other",
};

export const PROJECT_BUDGET_LABELS = {
  under_25k: "Under $25K",
  "25k_100k": "$25K–$100K",
  "100k_500k": "$100K–$500K",
  "500k_2m": "$500K–$2M",
  "2m_plus": "$2M+",
};

function titleFromSlug(value) {
  return String(value)
    .trim()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function normalizeListValue(raw) {
  if (raw == null || raw === "") return [];
  if (Array.isArray(raw)) return raw.map((v) => String(v).trim()).filter(Boolean);
  const s = String(raw).trim();
  if (!s) return [];
  if (s.includes(",")) return s.split(",").map((v) => v.trim()).filter(Boolean);
  return [s];
}

function isOtherEnumValue(value) {
  return String(value).trim().toLowerCase() === "other";
}

function formatEnumWithOther(raw, labelMap, otherText) {
  const list = normalizeListValue(raw);
  if (!list.length) {
    const custom = otherText != null ? String(otherText).trim() : "";
    return custom;
  }
  const custom = otherText != null ? String(otherText).trim() : "";
  return list
    .map((v) => {
      if (isOtherEnumValue(v)) return custom || labelMap.other || "Other";
      return labelMap[v] || labelMap[v.toLowerCase()] || titleFromSlug(v);
    })
    .filter(Boolean)
    .join(", ");
}

function pickString(data, ...keys) {
  for (const k of keys) {
    const v = data?.[k];
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return "";
}

/** Normalize raw Firestore profile/onboarding fields onto a contact row. */
export function enrichContactProfileFields(data) {
  if (!data || typeof data !== "object") return {};
  const companyName = pickString(data, "company_name", "companyName", "company", "organization");
  const phone = pickString(data, "phone", "mobile");
  const workRole = pickString(data, "workRole", "work_role");
  const workRoleOther = pickString(data, "workRoleOther", "work_role_other", "other_work_role");
  const projectTypes = normalizeListValue(data.projectTypes ?? data.project_type ?? data.project_types);
  const projectTypeOther = pickString(data, "projectTypeOther", "project_type_other", "other_project_type");
  const preferredFormat = pickString(data, "preferredFormat", "preferred_format");
  const preferredFormatOther = pickString(
    data,
    "preferredFormatOther",
    "preferred_format_other",
    "other_preferred_format"
  );
  const location = pickString(data, "location", "region");
  const companyWebsite = pickString(data, "companyWebsite", "company_website");
  const companyEmail = pickString(data, "companyEmail", "company_email");
  const teamSize = pickString(data, "teamSize", "team_size", "number_of_employees");
  const typicalProjectBudget = pickString(data, "typicalProjectBudget", "typical_project_budget");
  const asBuildsPerYear = pickString(data, "asBuildsPerYear", "as_builds_per_year");
  const onboardingVersion = data.onboardingVersion ?? data.onboarding_version ?? null;
  const onboardingComplete =
    data.onboardingComplete ??
    data.onboarding_complete ??
    (Number(onboardingVersion ?? 0) >= 1 ? true : null);
  const demoBooked = data.demoBooked ?? data.demo_booked ?? null;
  const lastScanAt = data.lastScanAt ?? data.last_scan_at ?? null;
  const totalScansFromProfile = data.totalScans ?? data.total_scans ?? null;

  return {
    companyName,
    phone,
    workRole,
    workRoleOther,
    projectTypes,
    projectTypeOther,
    preferredFormat,
    preferredFormatOther,
    location,
    companyWebsite,
    companyEmail,
    teamSize,
    typicalProjectBudget,
    asBuildsPerYear,
    onboardingVersion,
    onboardingComplete,
    demoBooked,
    lastScanAt,
    totalScansFromProfile,
  };
}

export function formatWorkRole(value, otherText) {
  return formatEnumWithOther(value, WORK_ROLE_LABELS, otherText);
}

export function formatProjectTypes(values, otherText) {
  return formatEnumWithOther(values, PROJECT_TYPE_LABELS, otherText);
}

export function formatPreferredFormat(value, otherText) {
  return formatEnumWithOther(value, PREFERRED_FORMAT_LABELS, otherText);
}

export function formatTypicalProjectBudget(value) {
  if (!value) return "";
  return PROJECT_BUDGET_LABELS[value] || PROJECT_BUDGET_LABELS[value.toLowerCase()] || titleFromSlug(value);
}

export function formatProfileScalar(fieldKey, value, contact) {
  if (value == null || value === "") return "";
  if (fieldKey === "workRole") return formatWorkRole(value, contact?.workRoleOther);
  if (fieldKey === "projectTypes") return formatProjectTypes(value, contact?.projectTypeOther);
  if (fieldKey === "preferredFormat") return formatPreferredFormat(value, contact?.preferredFormatOther);
  if (fieldKey === "typicalProjectBudget") return formatTypicalProjectBudget(value);
  if (fieldKey === "companyWebsite" && !/^https?:\/\//i.test(String(value))) {
    return String(value).trim();
  }
  return String(value).trim();
}

/** Rows for contact panel / cards — only fields with values. */
export function getContactProfileDetailRows(contact) {
  if (!contact) return { onboarding: [], profile: [] };
  const c = contact;

  const onboarding = [];
  const profile = [];

  const push = (target, label, value, fieldKey) => {
    const formatted = fieldKey ? formatProfileScalar(fieldKey, value, c) : String(value || "").trim();
    if (!formatted) return;
    target.push({ label, value: formatted });
  };

  push(onboarding, "Company", c.companyName);
  push(onboarding, "Profession", c.workRole, "workRole");
  if (c.companyEmail && c.companyEmail !== c.email) {
    push(onboarding, "Company email", c.companyEmail);
  }
  push(onboarding, "Project types", c.projectTypes, "projectTypes");
  push(onboarding, "Preferred format", c.preferredFormat, "preferredFormat");

  push(profile, "Phone", c.phone);
  push(profile, "Location", c.location);
  if (c.companyWebsite) {
    const href = /^https?:\/\//i.test(c.companyWebsite) ? c.companyWebsite : `https://${c.companyWebsite}`;
    profile.push({ label: "Company website", value: c.companyWebsite, href });
  }
  push(profile, "Team size", c.teamSize);
  push(profile, "Typical project budget", c.typicalProjectBudget, "typicalProjectBudget");
  push(profile, "As-builds per year", c.asBuildsPerYear);

  return { onboarding, profile };
}

/** Short subtitle for contact list rows (company · profession · project type · format). */
export function contactProfileSubtitle(contact) {
  if (!contact) return "";
  const parts = [
    contact.companyName,
    formatWorkRole(contact.workRole, contact.workRoleOther),
    formatProjectTypes(contact.projectTypes, contact.projectTypeOther),
    formatPreferredFormat(contact.preferredFormat, contact.preferredFormatOther),
  ].filter(Boolean);
  return parts.join(" · ");
}

export function hasContactProfileData(contact) {
  const { onboarding, profile } = getContactProfileDetailRows(contact);
  return onboarding.length > 0 || profile.length > 0;
}
